// 商户后台登录页 —— /login
// Principal / Password / 动态验证码（2026-10-31 口径变更：已绑定 TOTP 用户必填，未绑定可空）
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { Store, User, Lock, KeyRound } from 'lucide-react';
import { login, USE_MOCK } from '@/api/auth';
import { mapApiError } from '@/lib/apiErrorMessages';

export default function LoginPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [principal, setPrincipal] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  /** 登录后目标：优先回到来源页（守卫记录的 from），否则进 Dashboard */
  const goAfterLogin = () => {
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from || '/merchant/dashboard', { replace: true });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!principal.trim() || !password.trim()) {
      toast.error('请输入账号与密码');
      return;
    }
    setSubmitting(true);
    try {
      const res = await login(principal.trim(), password, otp.trim() || undefined);
      if (!res.ok) {
        toast.error(mapApiError(res.code, res.error));
        if (res.code === '3410') {
          // 已绑定 TOTP 用户验证码缺失/错误（MFA_OTP_REQUIRED / MFA_OTP_INVALID）：
          // 表单内增强提示并聚焦验证码框（不覆盖 toast 既有 mapApiError 行为）
          setOtpError('动态验证码缺失或错误，请输入 Google Authenticator 的 6 位验证码');
          otpInputRef.current?.focus();
        } else {
          setOtpError('');
        }
        return;
      }
      toast.success(USE_MOCK ? '登录成功（mock）' : '登录成功');
      const me = (await import('@/api/client')).loadSession()?.me;
      if (me && me.emailVerified === false) {
        navigate('/verify-email', { replace: true });
        return;
      }
      goAfterLogin();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-brand-600 rounded-xl flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-2xl text-white">Allinks 商户后台</span>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-white text-center mb-2">商户登录</h1>
          <p className="text-sm text-slate-400 text-center mb-6">登录至商户管理后台</p>

          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">账号</label>
              <div className="relative">
                <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-brand-500"
                  placeholder={USE_MOCK ? 'admin@fusheng.hk' : 'merchant01'}
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value)}
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">密码</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-brand-500"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Google 动态验证码（2FA）</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-brand-500"
                  placeholder="已开启 2FA 请输入 6 位动态验证码"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  autoComplete="one-time-code"
                />
              </div>
              {otpError && <p className="mt-1.5 text-xs text-red-400">{otpError}</p>}
            </div>
            <button type="submit" className="w-full btn-primary py-2.5" disabled={submitting}>
              {submitting ? '请稍候…' : '登录'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
